import { useContext, useEffect, useState } from 'react';
import { Toggle } from '@/shared/components/export';
import { Section } from '@/modules/articles/model/section';
import { WidgetContainer, WidgetContainerContext } from '@/core/widgets/export';
import { authService, useSessionId } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import { BaseScrollerState } from '@/modules/articles/widgets/scrollers/base-scroller';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';

const sanitizeName = (s: string) => {
  return s.replaceAll(/bullet(s)?|news/gi, '').trim();
};

function replaceCodes({
  list,
  includedSections,
}: {
  list: { codes: string[]; name: string }[];
  includedSections: Section[];
}) {
  const toAdd: { name: string }[] = [];
  const toFilter: string[] = [];
  let response = [...includedSections];
  list.forEach(item => {
    let isIncluded = true;
    item.codes.forEach(c => {
      isIncluded = !!includedSections.find(s => s.code === c) && isIncluded;
    });
    if (isIncluded) {
      toAdd.push({ name: item.name });
      item.codes.forEach(c => toFilter.push(c));
    }
  });
  toFilter.forEach(c => (response = response.filter(s => s.code !== c)));
  response = response.concat(toAdd as Section[]);
  return response.map(s => ({ name: s.name }));
}

export interface SectionFilterPreferences {
  includedSections: string[];
}

export default function SectionFilters({
  widgetId,
  sections,
  onFilteredSectionsChange,
  onFilteredDataNameChange,
  disabled,
}: {
  widgetId?: string;
  sections: Section[];
  onFilteredSectionsChange?: (s: string[]) => void;
  onFilteredDataNameChange: (s?: string) => void;
  disabled: boolean;
}) {
  const sessionId = useSessionId();
  const subject = authService.getSubjectData();

  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);

  const [state, setState] = useState<{ includedSections: string[]; dirty: boolean }>({
    includedSections:
      widgetContainer.getPreferences<SectionFilterPreferences>()?.includedSections ??
      sections.map(value => value.code),
    dirty: false,
  });

  useEffect(() => {
    if (!state) return;

    onFilteredSectionsChange?.(state.includedSections);
    // eslint-disable-next-line
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const titleSubstitutions = widgetContainer.getState<BaseScrollerState>()?.titleSubstitutions;
    if (state.includedSections.length > 4 || state.includedSections.length === sections.length)
      onFilteredDataNameChange(undefined);
    else {
      if (!sections) return;

      let remaining: Section[] | { name: string }[] = sections.filter(conceptItem => {
        const qcode = `${conceptItem.code}`;

        return state.includedSections.includes(qcode);
      });

      if (titleSubstitutions?.length)
        remaining = replaceCodes({
          list: titleSubstitutions,
          includedSections: remaining as Section[],
        });

      const remainingNames = remaining.map(value => sanitizeName(value.name));

      let title = '';

      if (remainingNames.length === 1) {
        title = remainingNames[0];
      } else if (remainingNames.length > 1 && remainingNames.length <= 4) {
        const front = remainingNames.slice(0, remainingNames.length - 1);
        const back = remainingNames.slice(remainingNames.length - 1, remainingNames.length);
        title = front.join(', ') + ' and ' + back;
      }

      onFilteredDataNameChange(title);
    }
    // eslint-disable-next-line
  }, [sections, state]);

  useSubscription<{ dashboardId: string }>(MessageServiceConstants.DASHBOARD_CHANGED, () => {
    // Reset state to match current widget container preferences or default to all sections
    const newIncludedSections =
      widgetContainer.getPreferences<SectionFilterPreferences>()?.includedSections ??
      sections.map(value => value.code);
    setState({ includedSections: newIncludedSections, dirty: false });
  });

  const filtersLeft = () => {
    return state?.includedSections.length || 1;
  };

  const excludeSubject = (s: string, exclude: boolean) => {
    console.log('Excluding', s, exclude);
    if (!state) return;

    if (exclude && filtersLeft() <= 1) return;

    if (disabled) return;

    let result: string[];
    // eslint-disable-next-line
    if (exclude) result = state.includedSections.filter(value => value != s);
    else result = [...state.includedSections, s];

    analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
      subject,
      object: {
        eventSubType: AnalyticsSubTypeEvent.SCROLLER_FILTERS_CHANGED,
        type: AnalyticsTypes.WIDGET,
        id: widgetId,
        config: result.join(', '),
      },
      session: {
        id: sessionId,
      },
    });

    setState({ includedSections: result, dirty: true });
    widgetContainer.updatePreferences<SectionFilterPreferences>({ includedSections: result });
  };

  const SubjectSwitch = ({ value }: { value: Section }) => {
    const checked = !!state?.includedSections.includes(value.code);
    const handleClick = () => {
      if (disabled) return;
      excludeSubject(value.code, checked);
    };

    return (
      <div
        role={'button'}
        tabIndex={disabled ? undefined : 0}
        className={`menu-list-item${disabled ? ' menu-list-item-disabled' : ''}`}
        onClick={handleClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
          }
        }}
      >
        <span className={'menu-list-item-label'}>{sanitizeName(value.name)}</span>
        <Toggle checked={checked} disabled={disabled} />
      </div>
    );
  };

  return (
    <>
      {sections.map(value => (
        <SubjectSwitch value={value} key={'ci' + value.code} />
      ))}
    </>
  );
}
